export const parseCsvRecords = (text: string): string[][] => {
	const records: string[][] = [];
	let currentRecord: string[] = [];
	let current = "";
	let inQuotes = false;

	const finishField = () => {
		currentRecord.push(current);
		current = "";
	};

	const finishRecord = () => {
		finishField();
		records.push(currentRecord);
		currentRecord = [];
	};

	for (let index = 0; index < text.length; index++) {
		const character = text[index];
		if (inQuotes) {
			if (character === '"') {
				if (index + 1 < text.length && text[index + 1] === '"') {
					current += '"';
					index++;
				} else {
					inQuotes = false;
				}
			} else if (character === "\r" && text[index + 1] === "\n") {
				current += "\n";
				index++;
			} else {
				current += character;
			}
		} else if (character === '"') {
			inQuotes = true;
		} else if (character === ",") {
			finishField();
		} else if (character === "\r" || character === "\n") {
			finishRecord();
			if (character === "\r" && text[index + 1] === "\n") index++;
		} else {
			current += character;
		}
	}

	if (current.length > 0 || currentRecord.length > 0) {
		finishRecord();
	}

	return records;
};

const isFiniteNumber = (value: string): boolean => {
	const trimmedValue = value.trim();
	return trimmedValue.length > 0 && Number.isFinite(Number(trimmedValue));
};

const isDateLike = (value: string): boolean => {
	const trimmedValue = value.trim();
	if (!trimmedValue || isFiniteNumber(trimmedValue)) return false;
	const hasDateShape = /[a-z]{3,}/i.test(trimmedValue) || /\d{1,4}[/-]\d{1,2}/.test(trimmedValue);
	return hasDateShape && !Number.isNaN(Date.parse(trimmedValue));
};

const isBooleanLike = (value: string): boolean => {
	return ["yes", "no", "true", "false", "1", "0"].includes(value.trim().toLowerCase());
};

export const detectHeaderRow = (records: string[][]): boolean => {
	const firstRow = records[0];
	if (!firstRow?.length || firstRow.some((value) => !value.trim() || isFiniteNumber(value) || isDateLike(value))) return false;
	if (records.length === 1) return true;
	return true;
};

const isTextualBoolean = (value: string): boolean => {
	return ["yes", "no", "true", "false"].includes(value.trim().toLowerCase());
};

export const inferColumnType = (values: string[]): "string" | "number" | "boolean" | "date" => {
	const nonEmptyValues = values.map((value) => value.trim()).filter(Boolean);
	if (nonEmptyValues.length === 0) return "string";
	// Only infer boolean when the column actually contains a textual boolean token
	// (yes/no/true/false). "1"/"0" are boolean-like but also valid numbers, so a column
	// of just those digits must stay a number — otherwise uploadDefaultData would coerce
	// those cells to true/false and permanently lose the numeric data.
	if (nonEmptyValues.some(isTextualBoolean) && nonEmptyValues.every(isBooleanLike)) return "boolean";
	if (nonEmptyValues.every(isFiniteNumber)) return "number";
	if (nonEmptyValues.every(isDateLike)) return "date";
	return "string";
};

export const normalizeCsvRecords = (records: string[][]): string[][] => {
	const columnCount = records.reduce((maximum, record) => Math.max(maximum, record.length), 0);
	return records.map((record) => Array.from({ length: columnCount }, (_, columnIndex) => record[columnIndex] ?? ""));
};
